import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  alpha,
  Button,
} from '@mui/material';
import {
  ShoppingCart as PlansIcon,
  AccountBalance as CreditsIcon,
  People as AccountsIcon,
  Settings as SettingsIcon,
  Description as TemplatesIcon,
  NewReleases as NovidadesIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import PlansManagement from '../components/admin/PlansManagement';
import { useAuth } from '../contexts/AuthContext';

const SuperAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [selectedOption, setSelectedOption] = React.useState<string | null>(null);

  // Verificar se é WhiteLabel baseado na rota ou propriedade do usuário
  const isWhiteLabel = location.pathname.includes('/whitelabel') || user?.isWhiteLabel || user?.role === 'whitelabel';

  const allAdminOptions = [
    {
      title: 'Planos',
      description: 'Gerenciar planos e assinaturas',
      icon: <PlansIcon sx={{ fontSize: 40 }} />,
      key: 'plans',
      color: '#3498db',
      restricted: true, // Apenas Super Admin
    },
    {
      title: 'Créditos',
      description: 'Gerenciar sistema de créditos',
      icon: <CreditsIcon sx={{ fontSize: 40 }} />,
      key: 'creditos',
      color: '#27ae60',
      restricted: false, // WhiteLabel pode ver
    },
    {
      title: 'Contas',
      description: 'Gerenciar contas e organizações',
      icon: <AccountsIcon sx={{ fontSize: 40 }} />,
      key: 'contas',
      color: '#e74c3c',
      restricted: false, // WhiteLabel pode ver
    },
    {
      title: 'Configurações',
      description: 'Configurar logo e domínio da organização',
      icon: <SettingsIcon sx={{ fontSize: 40 }} />,
      key: 'configuracoes',
      color: '#f39c12',
      restricted: false, // WhiteLabel pode ver
    },
    {
      title: 'Templates',
      description: 'Gerenciar templates do sistema',
      icon: <TemplatesIcon sx={{ fontSize: 40 }} />,
      key: 'templates',
      color: '#9b59b6',
      restricted: true, // Apenas Super Admin
    },
    {
      title: 'Novidades',
      description: 'Gerenciar novidades do sistema',
      icon: <NovidadesIcon sx={{ fontSize: 40 }} />,
      key: 'novidades',
      color: '#e67e22',
      restricted: true, // Apenas Super Admin
    },
  ];

  // Filtrar opções baseado no tipo de usuário
  const adminOptions = isWhiteLabel
    ? allAdminOptions.filter(option => !option.restricted)
    : allAdminOptions;

  const handleOptionClick = (key: string) => {
    if (key === 'plans') {
      setSelectedOption('plans');
    } else {
      const basePath = isWhiteLabel ? '/admin/whitelabel' : '/admin/super-admin';
      navigate(`${basePath}/${key}`);
    }
  };

  const handleBackToMenu = () => {
    setSelectedOption(null);
  };

  // If plans is selected, show the PlansManagement component
  if (selectedOption === 'plans') {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToMenu}
            sx={{ mr: 2, textTransform: 'none' }}
          >
            Voltar
          </Button>
          <PlansIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Planos
          </Typography>
        </Box>
        <PlansManagement />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: 'text.primary',
          mb: 4,
          textAlign: 'center'
        }}
      >
        {isWhiteLabel ? 'WhiteLabel' : 'Super Admin'}
      </Typography>

      <Typography
        variant="h6"
        color="text.secondary"
        sx={{
          mb: 4,
          textAlign: 'center',
          maxWidth: 600,
          mx: 'auto'
        }}
      >
        {isWhiteLabel
          ? 'Gerencie suas organizações e créditos'
          : 'Gerencie todas as configurações e funcionalidades administrativas do sistema'
        }
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 3,
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        {adminOptions.map((option, index) => (
          <Card
            key={index}
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
              },
              border: `1px solid ${alpha(option.color, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(option.color, 0.05)} 0%, ${alpha(option.color, 0.02)} 100%)`,
            }}
          >
            <CardActionArea
              onClick={() => handleOptionClick(option.key)}
              sx={{
                height: '100%',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: alpha(option.color, 0.05),
                },
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: alpha(option.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: option.color,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: alpha(option.color, 0.2),
                    transform: 'scale(1.1)',
                  },
                }}
              >
                {option.icon}
              </Box>

              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.primary',
                    mb: 1,
                  }}
                >
                  {option.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.5,
                  }}
                >
                  {option.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default SuperAdminPage;
